import React from "react";
import {useTranslation} from "react-i18next";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Tag} from "primereact/tag";
import {AppContext} from "../../../HomePage";
import {EmailCampaignService} from "../../../service/EmailCampaignService";

const STATUS_SEVERITY = {
    queued: 'info', sending: 'info', sent: 'success', partial: 'warning', failed: 'danger',
};

export default function EmailCampaignHistory({active}) {
    const {t} = useTranslation();
    const {setError} = React.useContext(AppContext);
    const service = React.useMemo(() => new EmailCampaignService(), []);

    const [items, setItems] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [pageSize] = React.useState(20);
    const [loading, setLoading] = React.useState(false);
    const [detail, setDetail] = React.useState(null);

    const load = React.useCallback((pageNum) => {
        setLoading(true);
        service.list({page: pageNum, page_size: pageSize})
            .then((data) => {
                setItems(data.items || []);
                setTotal(data.total || 0);
                setPage(data.page || pageNum);
            })
            .catch((e) => {
                setError(e);
                setError(null);
            })
            .finally(() => setLoading(false));
    }, [service, pageSize, setError]);

    React.useEffect(() => {
        if (active) load(1);
    }, [active, load]);

    const openDetail = (campaign) => {
        service.get(campaign.id)
            .then((data) => setDetail(data))
            .catch((e) => {
                setError(e);
                setError(null);
            });
    };

    const statusBody = (row) =>
        <Tag value={row.status} severity={STATUS_SEVERITY[row.status] || 'info'}/>;
    const countBody = (row, field) => (row.counts && row.counts[field]) || 0;
    const dateBody = (row) => row.created_at ? new Date(row.created_at).toLocaleString() : '';
    const actionBody = (row) =>
        <Button icon="pi pi-search" className="p-button-text" onClick={() => openDetail(row)}/>;

    return (
        <div>
            <DataTable value={items} loading={loading} paginator rows={pageSize}
                       totalRecords={total} lazy first={(page - 1) * pageSize}
                       onPage={(e) => load(Math.floor(e.first / pageSize) + 1)}
                       dataKey="id">
                <Column header={t('email_campaign.col_date')} body={dateBody}/>
                <Column field="subject" header={t('email_campaign.col_subject')}/>
                <Column field="from_email" header={t('email_campaign.col_sender')}/>
                <Column header={t('email_campaign.col_total')} body={(r) => countBody(r, 'total')}/>
                <Column header={t('email_campaign.col_sent')} body={(r) => countBody(r, 'sent')}/>
                <Column header={t('email_campaign.col_failed')} body={(r) => countBody(r, 'failed')}/>
                <Column header={t('email_campaign.col_skipped')} body={(r) => countBody(r, 'skipped')}/>
                <Column header={t('email_campaign.col_status')} body={statusBody}/>
                <Column body={actionBody}/>
            </DataTable>

            <Dialog header={t('email_campaign.detail')} visible={detail !== null}
                    style={{width: '720px'}} onHide={() => setDetail(null)}>
                {detail &&
                    <DataTable value={detail.recipients || []} paginator rows={20} dataKey="email">
                        <Column field="email" header="Email"/>
                        <Column field="status" header={t('email_campaign.col_status')}/>
                        <Column field="error" header="Error"/>
                    </DataTable>}
            </Dialog>
        </div>
    );
}
